import React, {
  useCallback, useEffect, useMemo, useState, Fragment, ReactElement,
} from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import StatusBar from '../../../components/status-bar/status-bar.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import Loader from '../../../components/loader/loader.component';
import PullToRefresh from '../../../components/pull-to-refresh/pull-to-refresh.component';
import LeaveConfirmModal from '../../../components/leave-confirm-modal/leave-confirm-modal.component';
import PageTabs from '../../../components/page-tabs/page-tabs.component';
import ContentHeader from '../../../components/content-header/content-header.component';
import Form from '../../../components/form/form.component';
import FormError from '../../../components/form-error/form-error.component';
import FormContainer from '../../../components/form-container/form-container.component';
import LimitedPageContainer from '../../../components/limited-page-block/limited-page-container.component';
import PageContainer from '../../../components/page-container/page-container.component';
import PageContainerSide from '../../../components/page-container-side/page-container-side.component';
import PageContainerMain from '../../../components/page-container-main/page-container-main.component';
import ImageContainer from '../../../components/image-container/image-container.component';
import Controls from '../../app/controls/controls.component';
import SettingsPageTabs from '../settings-page-tabs/settings-page-tabs.component';
import SettingsAccountSummary from './settings-account-summary/settings-account-summary.component';
import SettingsAccountPreferences from './settings-account-preferences/settings-account-preferences.component';
import SettingsAccountOkta from './settings-account-okta/settings-account-okta.component';
import SettingsAccountYardi from './settings-account-yardi/settings-account-yardi.component';
import SettingsAccountPowerBi from './settings-account-power-bi/settings-account-power-bi.component';
import SettingsAccountAzure from './settings-account-azure/settings-account-azure.component';
import SettingsAccountWidgets from './settings-account-widgets/settings-account-widgets.component';
import SettingsAccountStatisticItems
  from './settings-account-statistic-items/settings-account-statistic-items.component';
import SettingsAccountDealStatuses from './settings-account-deal-statuses/settings-account-deal-statuses.component';
import SettingsAccountDealTaskStatuses
  from './settings-account-deal-task-statuses/settings-account-deal-task-statuses.component';
import SettingsAccountActivityStatuses
  from './settings-account-activity-statuses/settings-account-activity-statuses.component';
import SettingsAccountDealRejectReasons
  from './settings-account-deal-reject-reasons/settings-account-deal-reject-reasons.component';
import SettingsAccountValidator from './settings-account.validator';
import ScreenService from '../../../services/screen-service';
import StructureService from '../../../services/structure-service';
import FormatService from '../../../services/format-service';
import FormService from '../../../services/form-service';
import useServerFormErrors from '../../../hooks/use-server-form-errors';
import useEditModeQuery from '../../../hooks/use-edit-mode-query';
import useFormBlock from '../../../hooks/use-form-block';
import useFormStructureModified from '../../../hooks/use-form-structure-modified';
import useScreenSmall from '../../../hooks/use-screen-small';
import {
  getAccountAssetEmailConnectorData, resetAssetConnectorAuthLink, saveAccountSettings, verifyAccountAssetEmailConnector,
} from './settings-account.action';
import { toggleEditMode } from '../../app/controls/controls.action';
import { getSettingsData } from '../settings.action';
import { resetFormErrors } from '../../app/app.action';
import { QUERY_PARAM_EMAIL_CONNECTOR_CODE, QUERY_PARAM_TAB } from '../../../constants/system/query-params';
import { FORM_ID_SETTINGS_ACCOUNT } from '../../../constants/system/form-ids';
import {
  ACCOUNT_ACTIVITY_STATUS_EDIT, ACCOUNT_DEAL_REJECT_REASON_EDIT, ACCOUNT_DEAL_STATUS_EDIT,
  ACCOUNT_DEAL_TASK_STATUS_EDIT, SETTINGS_ACCOUNT_EDIT, SETTINGS_ACCOUNT_VIEW,
} from '../../../constants/entity/permissions';
import {
  SETTING_NAME_ACCOUNT_STATISTIC_SETTINGS, SETTING_NAME_ACCOUNT_WIDGET_SETTINGS,
} from '../../../constants/entity/setting-names';
import {
  TAB_NAME_ACCOUNT, TAB_NAME_ACTIVITY_STATUSES, TAB_NAME_DEAD_DEAL_REASONS, TAB_NAME_DEAL_STATUSES,
  TAB_NAME_DEAL_TASK_STATUSES, TAB_NAME_OKTA, TAB_NAME_PREFERENCES, TAB_NAME_STATISTIC_ITEMS, TAB_NAME_SUMMARY,
  TAB_NAME_WIDGETS, TAB_NAME_YARDI, TAB_NAME_POWER_BI, TAB_NAME_AZURE,
} from '../../../constants/system/tab-names';
import { ACCOUNT_USER_TYPE_LIST_ADMIN } from '../../../constants/entity/account-user-type-lists';
import { ROUTE_SETTINGS_ACCOUNT } from '../../../constants/system/routes';
import { TAB_INDEX_DEFAULT } from '../../../constants/system/tab-indexes';
import CompanyDefaultImage from '../../../static/images/company-default-image.png';

const SettingsAccount: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const SETTING_JSON_LIST: string[] = [
    SETTING_NAME_ACCOUNT_WIDGET_SETTINGS,
    SETTING_NAME_ACCOUNT_STATISTIC_SETTINGS,
  ];

  const {
    isEditMode,
    isRightDrawerOpen,
    isDataLoaded,
    currentUser,
    accountData,
    accountSettingsData,
    assetConnectorAuthLink,
    roleList,
    accountSubscriptionPlanList,
    activityStatusList,
    dealStatusList,
    dealTaskStatusList,
    dealRejectReasonList,
    statisticItemList,
    widgetList,
    form: formProps,
    modified: formModifiedFields,
    handleSubmit: submitForm,
    pristine: isFormPristine,
    isSubmitting: isFormSaving,
    error: formError,
    values: formValues,
    hasValidationErrors: withFormErrors,
    formErrors: serverFormErrors,
    mainFormError: mainServerFormError,
  } = props;

  const {
    reset: resetForm,
    change: changeForm,
  } = formProps;

  const location: ReactRouterLocation = useLocation();
  const navigate: ReactRouterNavigate = useNavigate();
  const dispatch: ReduxDispatch = useDispatch();
  const isSmallScreen: boolean = useScreenSmall();
  const isFormStructureModified: boolean = useFormStructureModified(formModifiedFields, isDataLoaded);

  const [isOpenMessage, setIsOpenMessage]: [boolean, Function] = useState(false);
  const [statusMessage, setStatusMessage]: [string, Function] = useState('');
  const [isMessageError, setIsMessageError]: [boolean, Function] = useState(false);
  const [accountPhotoFile, setAccountPhotoFile]: [File | null, Function] = useState(null);
  const [isAccountPhotoDeleted, setIsAccountPhotoDeleted]: [boolean, Function] = useState(false);
  const [accountAttachments, setAccountAttachments]: [AttachmentInput[] | null, Function]
    = useState<StateValueDefault>(null);
  const [accountAttachmentFiles, setAccountAttachmentFiles]: [File[] | null, Function]
    = useState<StateValueDefault>(null);
  const [powerBiTemplateErrorMessage, setPowerBiTemplateErrorMessage]: [string | null, Function] = useState(null);
  const [powerBiTemplateFile, setPowerBiTemplateFile]: [File | null, Function] = useState(null);
  const [isPowerBiTemplateFileDeleted, setIsPowerBiTemplateFileDeleted]: [boolean, Function] = useState(false);
  const [isLeaveConfirmed, setIsLeaveConfirmed]: [boolean, Function] = useState(false);
  const [isLeaveConfirmModalOpen, setIsLeaveConfirmModalOpen]: [boolean, Function] = useState(false);
  const [lastLocation, setLastLocation]: [ReactRouterLocation | null, Function] = useState<StateValueDefault>(null);
  const [lastTabIndex, setLastTabIndex]: [number | null, Function] = useState(null);

  const locationQueryParams: ParsedQueryParams | null = FormatService.parseQueryParams(location.search);
  const queryTab: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_TAB]
    ? FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_TAB].toString()) : null;
  const queryEmailConnectorCode: string | null = locationQueryParams
  && locationQueryParams[QUERY_PARAM_EMAIL_CONNECTOR_CODE]
    ? locationQueryParams[QUERY_PARAM_EMAIL_CONNECTOR_CODE].toString() : null;

  const settingsAccountEditPermissions: string[] = useMemo((): string[] => [SETTINGS_ACCOUNT_EDIT], []);
  const settingsAccountViewPermissions: string[] = useMemo((): string[] => [SETTINGS_ACCOUNT_VIEW], []);
  const accountDealStatusEditPermissions: string[] = useMemo((): string[] => [ACCOUNT_DEAL_STATUS_EDIT], []);
  const accountDealTaskStatusEditPermissions: string[] = useMemo((): string[] => [ACCOUNT_DEAL_TASK_STATUS_EDIT], []);
  const accountActivityStatusEditPermissions: string[] = useMemo((): string[] => [ACCOUNT_ACTIVITY_STATUS_EDIT], []);
  const accountDealRejectReasonEditPermissions: string[] = useMemo((): string[] => [
    ACCOUNT_DEAL_REJECT_REASON_EDIT,
  ], []);
  const accountSettingsMap: Record<string, Setting> = useMemo((): Record<string, Setting> => (
    StructureService.getItemMap(accountSettingsData, 'name')
  ), [accountSettingsData]);

  const isCurrentUserAdmin: boolean = !!currentUser && !!currentUser.accountUserType
    && ACCOUNT_USER_TYPE_LIST_ADMIN.includes(currentUser.accountUserType);
  const isFormDataPristine: boolean = isFormPristine && !accountPhotoFile && !isAccountPhotoDeleted
    && !powerBiTemplateFile && !isPowerBiTemplateFileDeleted;
  const isFormChanged: boolean = isEditMode && !isFormDataPristine;
  const isFormStateChanged: boolean = isEditMode;
  const isSaveDisabled: boolean = isFormSaving || (isFormDataPristine && !isFormStructureModified);
  const isEditModeQueryDisabled: boolean = isLeaveConfirmed && (!!lastLocation || (lastTabIndex !== null
    && lastTabIndex >= TAB_INDEX_DEFAULT));
  const accountAttachmentItems: Attachment[] = accountData && accountData.attachments && accountData.attachments.length
    ? accountData.attachments : [];
  const accountId: number | null = accountData ? accountData.id : null;
  const formPrefix: string = 'account';
  const pageTitle: string = 'Settings: Account';
  const withServerErrors: boolean = serverFormErrors && !!Object.keys(serverFormErrors).length;

  const onCancelForm: Function = useCallback((): void => {
    FormService.forceResetForm(resetForm, formProps);

    setIsAccountPhotoDeleted(false);
    setIsPowerBiTemplateFileDeleted(false);
    setAccountPhotoFile(null);
    setPowerBiTemplateFile(null);
    setAccountAttachments(null);
    setAccountAttachmentFiles(null);
    dispatch(toggleEditMode(false));
  }, []);

  const onLeaveConfirm: Function = useCallback((): void => {
    setIsLeaveConfirmed(true);
    setIsLeaveConfirmModalOpen(false);

    onCancelForm();
  }, []);

  const closeLeaveConfirmModal: Function = useCallback((): void => {
    setIsLeaveConfirmModalOpen(false);
  }, []);

  const onAccountPhotoDelete: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setIsAccountPhotoDeleted(true);
    setAccountPhotoFile(null);
    changeForm(`${formPrefix}.photo`, null);

    event.stopPropagation();
  }, []);

  const onAccountPhotoChange: Function = useCallback((file: File): void => {
    setIsAccountPhotoDeleted(false);
    setAccountPhotoFile(file);
    changeForm(`${formPrefix}.photo`, file);
  }, []);

  const onAccountSummaryChange: Function = useCallback((
    event: React.ChangeEvent, accountSummaryValue: string,
  ): void => {
    changeForm(`${formPrefix}.summary`, accountSummaryValue);
  }, []);

  const onPowerBiTemplateFileChange: Function = useCallback((file: File): void => {
    setPowerBiTemplateFile(file);
    setPowerBiTemplateErrorMessage(null);
    setIsPowerBiTemplateFileDeleted(false);
    changeForm(`${formPrefix}.powerBiTemplateFile`, file);
  }, []);

  const onPowerBiTemplateFileDelete: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setPowerBiTemplateFile(null);
    setPowerBiTemplateErrorMessage(null);
    setIsPowerBiTemplateFileDeleted(true);
    changeForm(`${formPrefix}.powerBiTemplateFile`, null);

    event.stopPropagation();
  }, []);

  const onPowerBiTemplateFileError: Function = useCallback((error: string): void => {
    setPowerBiTemplateErrorMessage(error);
  }, []);

  const onFormLeave: FormLeaveFunction = useCallback((nextLocation: ReactRouterLocation): boolean => {
    if (isFormChanged) {
      setIsLeaveConfirmModalOpen(true);

      setLastLocation(nextLocation);

      return false;
    }

    if (isEditMode) {
      dispatch(toggleEditMode(false));
    }

    return true;
  }, [isFormChanged, isEditMode]);

  const onBeforeLocationChange: Function = useCallback((nextLocation: ReactRouterLocation): void => {
    if (isFormChanged) {
      setIsLeaveConfirmModalOpen(true);

      setLastLocation(nextLocation);
    }
  }, [isFormChanged]);

  const onBeforeTabChange: Function = useCallback((newTabIndex: number): void => {
    if (isFormChanged) {
      setIsLeaveConfirmModalOpen(true);

      setLastTabIndex(newTabIndex);
    }
  }, [isFormChanged]);

  const sendForm: Function = useCallback((values: FormValues): Promise<ActionCallback> | null => {
    if (!values) {
      return null;
    }

    const accountItemForm: FormValueItem = {
      ...values,
    };

    if (accountPhotoFile) {
      accountItemForm.account.photo = accountPhotoFile;
    } else if (isAccountPhotoDeleted) {
      accountItemForm.account.photo = null;
    } else {
      delete accountItemForm.account.photo;
    }

    if (powerBiTemplateFile) {
      accountItemForm.account.powerBiTemplateFile = powerBiTemplateFile;
    } else if (isPowerBiTemplateFileDeleted) {
      accountItemForm.account.powerBiTemplateFile = null;
    } else {
      delete accountItemForm.account.powerBiTemplateFile;
    }

    accountItemForm.accountSettingsData = Object.keys(accountItemForm.accountSettings)
      .map((settingName: string): FormValueItem => {
        const accountSettingsItem: Setting = accountItemForm.accountSettings[settingName];

        return {
          name: settingName,
          value: (accountSettingsItem.value && (
            SETTING_JSON_LIST.includes(settingName)
              ? FormatService.formatJsonString(accountSettingsItem.value) : accountSettingsItem.value.toString()
          )) || null,
        };
      });

    delete accountItemForm.accountSettings;

    accountItemForm.accountDealStatuses = accountItemForm.accountDealStatuses
    && !!Object.keys(accountItemForm.accountDealStatuses).length
      ? Object.values(accountItemForm.accountDealStatuses)
        .filter((accountDealStatus: FormValueItem): boolean => !!accountDealStatus.title) : [];

    accountItemForm.accountDealTaskStatuses = accountItemForm.accountDealTaskStatuses
    && !!Object.keys(accountItemForm.accountDealTaskStatuses).length
      ? Object.values(accountItemForm.accountDealTaskStatuses)
        .filter((accountDealTaskStatus: FormValueItem): boolean => !!accountDealTaskStatus.title)
      : [];

    accountItemForm.accountActivityStatuses = accountItemForm.accountActivityStatuses
    && !!Object.keys(accountItemForm.accountActivityStatuses).length
      ? Object.values(accountItemForm.accountActivityStatuses)
        .filter((accountActivityStatus: FormValueItem): boolean => !!accountActivityStatus.title)
      : [];

    accountItemForm.accountDealRejectReasons = accountItemForm.accountDealRejectReasons
    && !!Object.keys(accountItemForm.accountDealRejectReasons).length
      ? Object.values(accountItemForm.accountDealRejectReasons)
        .filter((accountDealRejectReason: FormValueItem): boolean => !!accountDealRejectReason.title)
      : [];

    const accountForm: AccountFormInput = accountItemForm;

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(saveAccountSettings({
        accountForm: accountForm,
      }, (response: ActionResponse): void => {
        onCancelForm();

        setStatusMessage('Saved successfully');
        setIsOpenMessage(true);
        setIsMessageError(false);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          submitForm();
        }
      }));
    });
  }, [isSaveDisabled, accountPhotoFile, isAccountPhotoDeleted, powerBiTemplateFile, isPowerBiTemplateFileDeleted]);

  const onSubmitForm: Function = useCallback((): void => {
    submitForm();

    if (!withFormErrors) {
      sendForm(formValues);
    } else {
      FormService.focusOnFirstFormError();
    }
  }, [
    isSaveDisabled, accountPhotoFile, isAccountPhotoDeleted, powerBiTemplateFile, isPowerBiTemplateFileDeleted,
    withFormErrors, formValues,
  ]);

  const assetConnectEmailClick: Function = useCallback((accountId: number): void => {
    if (accountId) {
      dispatch(getAccountAssetEmailConnectorData({
        accountId,
      }));
    }
  }, []);

  const closeMessage: Function = useCallback((): void => {
    setIsMessageError(false);
    setIsOpenMessage(false);
    setStatusMessage('');
  }, []);

  const onAccountAttachmentFilesChange: Function = useCallback((files: File[]): void => {
    const newFiles: File[] = [...(accountAttachmentFiles || []), ...files];

    setAccountAttachmentFiles(newFiles);
    changeForm(`${formPrefix}.attachmentFiles`, newFiles);
  }, [accountAttachmentFiles]);

  const onAccountAttachmentFileDelete: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, fileIndex: number,
  ): void => {
    if (accountAttachments) {
      if (fileIndex > accountAttachments.length) {
        const newAccountAttachmentFiles: File[] = [...(accountAttachmentFiles || [])];

        StructureService.deleteListItemByIndex(newAccountAttachmentFiles, fileIndex);
        setAccountAttachmentFiles(newAccountAttachmentFiles);
        changeForm(`${formPrefix}.attachmentFiles`, newAccountAttachmentFiles);
      } else {
        const newAccountAttachments: AttachmentInput[] = [...(accountAttachments || [])];

        StructureService.deleteListItemByIndex(newAccountAttachments, fileIndex - accountAttachments.length);
        setAccountAttachments(newAccountAttachments);
        changeForm(`${formPrefix}.attachments`, newAccountAttachments);
      }
    }
  }, [accountAttachmentFiles, accountAttachments]);

  const onAccountAttachmentFileError: Function = useCallback((errorText: string): void => {
    setStatusMessage(errorText);
    setIsOpenMessage(true);
    setIsMessageError(true);
  }, []);

  const tabs: TabItem[] = [
    {
      id: 1,
      name: TAB_NAME_SUMMARY,
      title: 'Summary',
      editPermissions: settingsAccountEditPermissions,
      component: (
        <SettingsAccountSummary
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          currentUser={currentUser}
          formError={formError}
          accountData={accountData}
          accountAttachments={accountAttachments}
          accountAttachmentFiles={accountAttachmentFiles}
          accountSubscriptionPlanList={accountSubscriptionPlanList}
          roleList={roleList}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
          onAccountSummaryChange={onAccountSummaryChange}
          onAccountAttachmentFileError={onAccountAttachmentFileError}
          onAccountAttachmentFileDelete={onAccountAttachmentFileDelete}
          onAccountAttachmentFilesChange={onAccountAttachmentFilesChange}
        />
      ),
    },
    {
      id: 2,
      name: TAB_NAME_PREFERENCES,
      title: 'Preferences',
      isVisible: isCurrentUserAdmin,
      editPermissions: settingsAccountEditPermissions,
      component: (
        <SettingsAccountPreferences
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          accountSettingsMap={accountSettingsMap}
          formError={formError}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
        />
      ),
    },
    {
      id: 3,
      name: TAB_NAME_OKTA,
      title: 'Okta',
      editPermissions: settingsAccountEditPermissions,
      component: (
        <SettingsAccountOkta
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          formError={formError}
          accountData={accountData}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
        />
      ),
    },
    {
      id: 4,
      name: TAB_NAME_YARDI,
      title: 'Yardi',
      editPermissions: settingsAccountEditPermissions,
      component: (
        <SettingsAccountYardi
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          accountData={accountData}
          formError={formError}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
          onAssetConnectEmailClick={assetConnectEmailClick}
        />
      ),
    },
    {
      id: 5,
      name: TAB_NAME_POWER_BI,
      title: 'Power BI',
      editPermissions: settingsAccountEditPermissions,
      component: (
        <SettingsAccountPowerBi
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          formError={formError}
          currentUser={currentUser}
          accountData={accountData}
          isPowerBiTemplateFileDeleted={isPowerBiTemplateFileDeleted}
          powerBiTemplateFile={powerBiTemplateFile}
          powerBiTemplateErrorMessage={powerBiTemplateErrorMessage}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
          onPowerBiTemplateFileChange={onPowerBiTemplateFileChange}
          onPowerBiTemplateFileDelete={onPowerBiTemplateFileDelete}
          onPowerBiTemplateFileError={onPowerBiTemplateFileError}
        />
      ),
    },
    {
      id: 6,
      name: TAB_NAME_AZURE,
      title: 'Azure',
      editPermissions: settingsAccountEditPermissions,
      component: (
        <SettingsAccountAzure
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          formError={formError}
          currentUser={currentUser}
          accountData={accountData}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
        />
      ),
    },
    {
      id: 7,
      name: TAB_NAME_WIDGETS,
      title: 'Widgets',
      editPermissions: settingsAccountEditPermissions,
      component: (
        <SettingsAccountWidgets
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          accountSettingsMap={accountSettingsMap}
          widgetList={widgetList}
          formError={formError}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
        />
      ),
    },
    {
      id: 8,
      name: TAB_NAME_STATISTIC_ITEMS,
      title: 'Statistic Items',
      editPermissions: settingsAccountEditPermissions,
      component: (
        <SettingsAccountStatisticItems
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          accountSettingsMap={accountSettingsMap}
          statisticItemList={statisticItemList}
          formError={formError}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
        />
      ),
    },
    {
      id: 9,
      name: TAB_NAME_DEAL_STATUSES,
      title: 'Deal Statuses',
      isVisible: !isSmallScreen,
      editPermissions: accountDealStatusEditPermissions,
      component: (
        <SettingsAccountDealStatuses
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          accountData={accountData}
          dealStatusList={dealStatusList}
          formError={formError}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
        />
      ),
    },
    {
      id: 10,
      name: TAB_NAME_DEAL_TASK_STATUSES,
      title: 'Deal Task Statuses',
      isVisible: !isSmallScreen,
      editPermissions: accountDealTaskStatusEditPermissions,
      component: (
        <SettingsAccountDealTaskStatuses
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          accountData={accountData}
          dealTaskStatusList={dealTaskStatusList}
          formError={formError}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
        />
      ),
    },
    {
      id: 11,
      name: TAB_NAME_ACTIVITY_STATUSES,
      title: 'Activity Statuses',
      isVisible: !isSmallScreen,
      editPermissions: accountActivityStatusEditPermissions,
      component: (
        <SettingsAccountActivityStatuses
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          accountData={accountData}
          activityStatusList={activityStatusList}
          formError={formError}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
        />
      ),
    },
    {
      id: 12,
      name: TAB_NAME_DEAD_DEAL_REASONS,
      title: 'Dead Deal Reasons',
      isVisible: !isSmallScreen,
      editPermissions: accountDealRejectReasonEditPermissions,
      component: (
        <SettingsAccountDealRejectReasons
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          accountData={accountData}
          dealRejectReasonList={dealRejectReasonList}
          formError={formError}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
        />
      ),
    },
  ];

  const onTabChange: Function = useCallback((newTabId: number): void => {
    const queryParams: LocationQueryParams = locationQueryParams || {};

    onCancelForm();

    tabs.forEach((tabItem: TabItem): void => {
      if (tabItem.id === newTabId) {
        queryParams[QUERY_PARAM_TAB] = tabItem.name;

        navigate({
          pathname: ROUTE_SETTINGS_ACCOUNT,
          search: FormatService.formatQueryParams(queryParams),
        });
      }
    });
  }, [locationQueryParams, tabs]);

  const onRefresh: Function = useCallback((): void => {
    dispatch(getSettingsData());
  }, []);

  const onFormChange: Function = useCallback((event: React.SyntheticEvent): void => {
    const fieldName: string = event.target && (event.target as HTMLInputElement).name;

    if (withServerErrors && fieldName) {
      dispatch(resetFormErrors());
      FormService.resetFormServerErrors(formProps, fieldName);
      submitForm();
    }
  }, [withServerErrors, formProps]);

  useEffect((): void => {
    dispatch(getSettingsData());
  }, []);

  useEffect((): void => {
    if (isEditMode && !accountAttachments) {
      setAccountAttachments(accountAttachmentItems.map((attachmentItem: Attachment): AttachmentInput => ({
        id: attachmentItem.id,
        name: attachmentItem.file.name,
        link: attachmentItem.file.linkName,
      })));
      setAccountAttachmentFiles([]);
    }
  }, [isEditMode, accountAttachmentItems]);

  useEffect((): void => {
    if (assetConnectorAuthLink) {
      dispatch(resetAssetConnectorAuthLink());

      ScreenService.openLink(assetConnectorAuthLink);
    }
  }, [assetConnectorAuthLink]);

  useEffect((): void => {
    if (queryEmailConnectorCode && accountId) {
      const queryParams: LocationQueryParams = {};

      dispatch(verifyAccountAssetEmailConnector({
        verificationForm: {
          code: queryEmailConnectorCode,
          accountId,
        },
      }, (): void => {
        navigate({
          pathname: ROUTE_SETTINGS_ACCOUNT,
          search: FormatService.formatQueryParams(queryParams),
        });
      }));
    }
  }, [queryEmailConnectorCode, accountId]);

  useEffect((): void => {
    if (isLeaveConfirmed) {
      if (lastLocation) {
        navigate({
          pathname: lastLocation.pathname,
          search: lastLocation.search || '',
        });

        setLastLocation(undefined);
      }

      if (lastTabIndex !== null && lastTabIndex >= TAB_INDEX_DEFAULT) {
        const queryParams: LocationQueryParams = locationQueryParams || {};
        const currentTab: TabItem = tabs[lastTabIndex];

        if (currentTab) {
          queryParams[QUERY_PARAM_TAB] = currentTab.name;

          navigate({
            pathname: ROUTE_SETTINGS_ACCOUNT,
            search: FormatService.formatQueryParams(queryParams),
          });
        }

        setLastTabIndex(null);
      }

      setIsLeaveConfirmed(false);
    }
  }, [isLeaveConfirmed]);

  useEffect((): void => {
    if (lastLocation) {
      setLastTabIndex(null);
    }
  }, [lastLocation]);

  useServerFormErrors(serverFormErrors, (): void => {
    submitForm();
  });

  useEditModeQuery(isEditModeQueryDisabled);

  useFormBlock(onFormLeave, isFormChanged, isFormStateChanged, isLeaveConfirmed, isLeaveConfirmModalOpen);

  return (
    <CheckAccess permissions={isEditMode ? settingsAccountEditPermissions : settingsAccountViewPermissions}>
      <Form id={FORM_ID_SETTINGS_ACCOUNT} onChange={onFormChange}>
        <PullToRefresh isDisabled={isEditMode} onRefresh={onRefresh}>
          <ContentHeader title={pageTitle} />
          <SettingsPageTabs
            isDisabled={isEditMode}
            isCompressed={isRightDrawerOpen}
            activeTab={TAB_NAME_ACCOUNT}
            currentUser={currentUser}
            onBeforeLocationChange={onBeforeLocationChange}
          />
          {
            !isDataLoaded && (
              <Loader isPageLoader withTopOffset />
            )
          }
          {
            isDataLoaded && (
              <Fragment>
                <Controls
                  isSaveDisabled={isSaveDisabled}
                  editPermissions={settingsAccountEditPermissions}
                  onCancelForm={onCancelForm}
                  onSubmitForm={onSubmitForm}
                />
                <PageContainer>
                  <PageContainerSide>
                    <ImageContainer
                      isImageDeleted={isAccountPhotoDeleted}
                      isEditMode={isEditMode}
                      name={accountData.name}
                      image={accountData.photo}
                      imageThumbnail={accountData.photoThumbnail}
                      imageFile={accountPhotoFile}
                      imageFormFieldName={`${formPrefix}.photo`}
                      defaultImage={CompanyDefaultImage}
                      onImageFileChange={onAccountPhotoChange}
                      onImageFileDelete={onAccountPhotoDelete}
                    />
                  </PageContainerSide>
                  <PageContainerMain>
                    <LimitedPageContainer isActive>
                      <PageTabs
                        isCompressed
                        isDisabled={isFormChanged}
                        tabs={tabs}
                        currentTab={queryTab}
                        onTabChange={onTabChange}
                        onBeforeTabChange={onBeforeTabChange}
                      />
                    </LimitedPageContainer>
                  </PageContainerMain>
                </PageContainer>
              </Fragment>
            )
          }
          {
            (!!formError || !!mainServerFormError) && !isFormSaving && (
              <FormError
                withShowError={isEditMode}
                withLeftOffset={isRightDrawerOpen}
                error={formError || mainServerFormError}
              />
            )
          }
        </PullToRefresh>
        <LeaveConfirmModal
          isOpen={isLeaveConfirmModalOpen}
          onConfirm={onLeaveConfirm}
          onCloseModal={closeLeaveConfirmModal}
        />
        <StatusBar isOpen={isOpenMessage} isError={isMessageError} message={statusMessage} onClose={closeMessage} />
      </Form>
    </CheckAccess>
  );
});

const initialValues: FormValues = {};
const mapState: ReduxMapStateCallback = (reducers: Reducers): ReduxMapState => {
  const {
    settings,
    controls,
    app,
    rightDrawer,
  } = reducers;

  const accountData: Account = settings.settingsData && settings.settingsData.accountData;
  const accountSettingsData: Setting[] = settings.settingsData && settings.settingsData.accountSettingsData;
  const dealStatusList: DealStatus[] = settings.settingsData && settings.settingsData.dealStatusList;
  const dealTaskStatusList: DealTaskStatus[] = settings.settingsData && settings.settingsData.dealTaskStatusList;
  const activityStatusList: ActivityStatus[] = settings.settingsData && settings.settingsData.activityStatusList;
  const dealRejectReasonList: DealRejectReason[] = settings.settingsData && settings.settingsData.dealRejectReasonList;

  if (accountData) {
    initialValues.account = {
      id: accountData.id,
      name: accountData.name,
      shortName: accountData.shortName,
      role: accountData.role && accountData.role.name,
      accountSubscriptionPlan: accountData.accountSubscriptionPlan && accountData.accountSubscriptionPlan.name,
      summary: accountData.summary || null,
      oktaAccountName: accountData.oktaAccountName || null,
      oktaAccountClientId: accountData.oktaAccountClientId || null,
      yardiConnectorEmail: accountData.yardiConnectorEmail || null,
      powerBiIsEnabled: accountData.powerBiIsEnabled || false,
      powerBiTenantId: accountData.powerBiTenantId || null,
      powerBiClientId: accountData.powerBiClientId || null,
      powerBiClientSecret: accountData.powerBiClientSecret || null,
      powerBiGroupId: accountData.powerBiGroupId || null,
      powerBiProjectId: accountData.powerBiProjectId || null,
      powerBiDatasetId: accountData.powerBiDatasetId || null,
      powerBiTemplateFile: null,
      azureIsEnabled: accountData.azureIsEnabled || false,
      azureBlobStorageAccountName: accountData.azureBlobStorageAccountName || null,
      azureBlobStorageAccountKey: accountData.azureBlobStorageAccountKey || null,
      azureBlobStorageContainerName: accountData.azureBlobStorageContainerName || null,
      azureSqlServerAccountName: accountData.azureSqlServerAccountName || null,
      azureSqlServerPort: accountData.azureSqlServerPort || null,
      azureSqlServerUserName: accountData.azureSqlServerUserName || null,
      azureSqlServerPassword: accountData.azureSqlServerPassword || null,
      azureSqlServerDatabaseName: accountData.azureSqlServerDatabaseName || null,
      azureSqlServerDatabaseSchema: accountData.azureSqlServerDatabaseSchema || null,
      azureSqlServerDatasourceName: accountData.azureSqlServerDatasourceName || null,
      azureSqlServerStoredProcedureRefreshName: accountData.azureSqlServerStoredProcedureRefreshName || null,
      photo: null,
      attachments: accountData.attachments && accountData.attachments.length
        ? accountData.attachments.map((attachmentItem: Attachment): AttachmentInput => ({
          id: attachmentItem.id,
          name: attachmentItem.file.name,
          link: attachmentItem.file.linkName,
        })) : [],
      attachmentFiles: [],
    };

    initialValues.accountSettings = {};
    if (accountSettingsData && accountSettingsData.length) {
      accountSettingsData.forEach((accountSetting: Setting): void => {
        initialValues.accountSettings[accountSetting.name] = {
          value: accountSetting.value,
        };
      });
    }

    initialValues.accountDealStatuses = {};
    if (accountData.accountDealStatuses && dealStatusList) {
      dealStatusList.forEach((dealStatusItem: DealStatus): void => {
        const formKeyDealStatus: string = FormService.getIndexedFormFieldKey(dealStatusItem.id);
        const accountDealStatus: AccountDealStatus | null = accountData.accountDealStatuses.find((
          accountDealStatusItem: AccountDealStatus,
        ): boolean => (
          accountDealStatusItem.status.name === dealStatusItem.name
        )) || null;

        initialValues.accountDealStatuses[formKeyDealStatus] = {
          id: accountDealStatus ? accountDealStatus.id : null,
          status: accountDealStatus && accountDealStatus.status
            ? accountDealStatus.status.name : dealStatusItem.name,
          title: accountDealStatus ? accountDealStatus.title : null,
          defaultCollapsed: accountDealStatus ? accountDealStatus.defaultCollapsed : false,
        };
      });
    }

    initialValues.accountDealTaskStatuses = {};
    if (accountData.accountDealTaskStatuses && dealTaskStatusList) {
      dealTaskStatusList.forEach((dealTaskStatusItem: DealTaskStatus): void => {
        const formKeyDealTaskStatus: string = FormService.getIndexedFormFieldKey(dealTaskStatusItem.id);
        const accountDealTaskStatus: AccountDealTaskStatus | null = accountData.accountDealTaskStatuses
          .find((accountDealTaskStatusItem: AccountDealTaskStatus): boolean => (
            accountDealTaskStatusItem.status.name === dealTaskStatusItem.name
          )) || null;

        initialValues.accountDealTaskStatuses[formKeyDealTaskStatus] = {
          id: accountDealTaskStatus ? accountDealTaskStatus.id : null,
          status: accountDealTaskStatus && accountDealTaskStatus.status
            ? accountDealTaskStatus.status.name : dealTaskStatusItem.name,
          title: accountDealTaskStatus ? accountDealTaskStatus.title : null,
        };
      });
    }

    initialValues.accountActivityStatuses = {};
    if (accountData.accountActivityStatuses && activityStatusList) {
      activityStatusList.forEach((activityStatusItem: ActivityStatus): void => {
        const formKeyActivityStatus: string = FormService.getIndexedFormFieldKey(activityStatusItem.id);
        const accountActivityStatus: AccountActivityStatus | null = accountData.accountActivityStatuses
          .find((accountActivityStatusItem: AccountActivityStatus): boolean => (
            accountActivityStatusItem.status.name === activityStatusItem.name
          )) || null;

        initialValues.accountActivityStatuses[formKeyActivityStatus] = {
          id: accountActivityStatus ? accountActivityStatus.id : null,
          status: accountActivityStatus && accountActivityStatus.status
            ? accountActivityStatus.status.name : activityStatusItem.name,
          title: accountActivityStatus ? accountActivityStatus.title : null,
        };
      });
    }

    initialValues.accountDealRejectReasons = {};
    if (accountData.accountDealRejectReasons && dealRejectReasonList) {
      dealRejectReasonList.forEach((dealRejectReasonItem: DealRejectReason): void => {
        const formKeyDealRejectReason: string = FormService.getIndexedFormFieldKey(dealRejectReasonItem.id);
        const accountDealRejectReason: AccountDealRejectReason | null = accountData.accountDealRejectReasons
          .find((accountDealRejectReasonItem: AccountDealRejectReason): boolean => (
            accountDealRejectReasonItem.rejectReason.name === dealRejectReasonItem.name
          )) || null;

        initialValues.accountDealRejectReasons[formKeyDealRejectReason] = {
          id: accountDealRejectReason ? accountDealRejectReason.id : null,
          rejectReason: accountDealRejectReason && accountDealRejectReason.rejectReason
            ? accountDealRejectReason.rejectReason.name : dealRejectReasonItem.name,
          title: accountDealRejectReason ? accountDealRejectReason.title : null,
        };
      });
    }
  }

  return {
    isDataLoaded: !!settings.settingsData,
    isEditMode: controls.isEditMode,
    isRightDrawerOpen: rightDrawer.isDrawerOpen,
    currentUser: app.baseData && app.baseData.currentUser,
    accountData: settings.settingsData && settings.settingsData.accountData,
    accountSettingsData: settings.settingsData && settings.settingsData.accountSettingsData,
    assetConnectorAuthLink: settings.assetConnectorAuthLink,
    roleList: settings.settingsData && settings.settingsData.roleList,
    accountSubscriptionPlanList: settings.settingsData && settings.settingsData.accountSubscriptionPlanList,
    activityStatusList: settings.settingsData && settings.settingsData.activityStatusList,
    dealStatusList: settings.settingsData && settings.settingsData.dealStatusList,
    dealTaskStatusList: settings.settingsData && settings.settingsData.dealTaskStatusList,
    dealRejectReasonList: settings.settingsData && settings.settingsData.dealRejectReasonList,
    statisticItemList: settings.settingsData && settings.settingsData.statisticItemList,
    widgetList: settings.settingsData && settings.settingsData.widgetList,
  };
};

const SettingsAccountForm: ReactComponent = (props: ReactComponentProps): ReactElement => {
  const formErrors: ValidationErrors = useSelector(({ settings }: Reducers): ValidationErrorsMap => (
    settings.formErrors
  ));
  const isFormSaving: ValidationErrors = useSelector(({ settings }: Reducers): ValidationErrorsMap => (
    settings.isFormSaving
  ));

  return (
    <FormContainer
      formId={FORM_ID_SETTINGS_ACCOUNT}
      component={SettingsAccount}
      validator={SettingsAccountValidator}
      initialValues={initialValues}
      formErrors={formErrors}
      isSubmitting={isFormSaving}
      {...props}
    />
  );
};

export default connect(mapState)(SettingsAccountForm);
